/*
 * Раскрываемый раздел левой панели (DevOps, MLOps, Базы данных, …): заголовок — кнопка со стрелкой,
 * пункты — под ним, с отступом в раскрытой панели. Раздел, в котором открыта текущая страница,
 * раскрывается сам; выбор пользователя (раскрыт/свёрнут) запоминается в браузере.
 */
import { PropsWithChildren, useEffect, useState } from 'react';
import { Collapse, makeStyles } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useLocation } from 'react-router-dom';
import { IconComponent } from '@backstage/core-plugin-api';
import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  // пункты раздела — со сдвигом вправо, пока панель раскрыта (в свёрнутой видны только иконки)
  nested: { paddingLeft: theme.spacing(2) },
  chevron: { display: 'flex', alignItems: 'center', opacity: 0.7 },
}));

const storageKey = (id: string) => `viva.sidebar.section.${id}`;

const readStored = (id: string): boolean | undefined => {
  try {
    const v = window.localStorage.getItem(storageKey(id));
    return v === null ? undefined : v === 'open';
  } catch {
    return undefined;
  }
};

const writeStored = (id: string, open: boolean) => {
  try {
    window.localStorage.setItem(storageKey(id), open ? 'open' : 'closed');
  } catch {
    // приватный режим или запрет хранилища — раздел просто не запоминается
  }
};

export type SidebarSectionProps = PropsWithChildren<{
  /** ключ в localStorage */
  id: string;
  icon: IconComponent;
  text: string;
  /** префиксы адресов пунктов раздела: на этих страницах раздел раскрыт */
  paths: string[];
}>;

export const SidebarSection = ({
  id,
  icon,
  text,
  paths,
  children,
}: SidebarSectionProps) => {
  const classes = useStyles();
  const { isOpen: sidebarOpen } = useSidebarOpenState();
  const { pathname } = useLocation();
  const current = paths.some(
    p => pathname === p || pathname.startsWith(`${p}/`),
  );
  const [open, setOpen] = useState<boolean>(() => readStored(id) ?? current);

  // переход на страницу раздела (ссылка, поиск, главная) — раздел раскрывается
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  const toggle = () => {
    setOpen(prev => {
      writeStored(id, !prev);
      return !prev;
    });
  };

  return (
    <>
      <SidebarItem icon={icon} text={text} onClick={toggle}>
        <span className={classes.chevron}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </span>
      </SidebarItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={sidebarOpen ? classes.nested : undefined}>
          {children}
        </div>
      </Collapse>
    </>
  );
};
